/**
 * Calendar Utilities
 *
 * Pure, framework-agnostic helpers for the Calendar feature: deriving month
 * cache keys, working out which months a date range touches, and grouping /
 * filtering events by day. Shared by the calendar store and the web views (and
 * available for the future mobile port).
 */

import { dayjs } from './date';

import type { DateInput } from './date';
import type { CalendarEvent } from '../types/domain/calendar.types';

/** Format used for month cache keys. */
export const CALENDAR_MONTH_KEY_FORMAT = 'YYYY-MM';

/** The month cache key (`YYYY-MM`) a date belongs to. */
export const getMonthKey = (date: DateInput): string =>
  dayjs(date).format(CALENDAR_MONTH_KEY_FORMAT);

/**
 * Every month cache key (`YYYY-MM`) touched by the inclusive `[from, to]` date
 * range. A week that straddles a month boundary therefore yields two keys.
 */
export const getMonthKeysInRange = (
  from: DateInput,
  to: DateInput
): string[] => {
  const end = dayjs(to).startOf('month');
  let cursor = dayjs(from).startOf('month');
  const keys: string[] = [];
  while (cursor.isSameOrBefore(end, 'month')) {
    keys.push(cursor.format(CALENDAR_MONTH_KEY_FORMAT));
    cursor = cursor.add(1, 'month');
  }
  return keys;
};

/**
 * Filter events to those whose day falls within the inclusive `[from, to]`
 * range.
 */
export const filterEventsInRange = (
  events: CalendarEvent[],
  from: DateInput,
  to: DateInput
): CalendarEvent[] => {
  const start = dayjs(from).startOf('day');
  const end = dayjs(to).endOf('day');
  return events.filter((event) => {
    const day = dayjs(event.date);
    return day.isSameOrAfter(start) && day.isSameOrBefore(end);
  });
};

/** Group events by their `date` (`YYYY-MM-DD`). */
export const groupEventsByDate = (
  events: CalendarEvent[]
): Record<string, CalendarEvent[]> => {
  const grouped: Record<string, CalendarEvent[]> = {};
  for (const event of events) {
    if (!grouped[event.date]) grouped[event.date] = [];
    grouped[event.date].push(event);
  }
  return grouped;
};
